import type { ChartElementType } from '@shared/types';

export interface MeasureOptions {
  text: string;
  fontSize: number;
  fontFamily?: string;
  fontWeight?: string;
  fontStyle?: string;
  paddingX?: number;
  paddingY?: number;
  lineHeight?: number;
  minWidth?: number;
  maxWidth?: number;
  skipTenCharMin?: boolean;
}

export interface ElementSize {
  width: number;
  height: number;
}

let _canvas: HTMLCanvasElement | null = null;
function getCtx(font: string): CanvasRenderingContext2D | null {
  try {
    if (!_canvas) _canvas = document.createElement('canvas');
    const ctx = _canvas.getContext('2d');
    if (!ctx) return null;
    ctx.font = font;
    return ctx;
  } catch {
    return null;
  }
}

function singleLineWidth(
  text: string,
  fontSize: number,
  fontWeight: string,
  fontStyle: string,
  fontFamily: string,
): number {
  const font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
  const ctx = getCtx(font);
  if (!ctx) return text.length * fontSize * 0.55;
  return ctx.measureText(text).width;
}

export function measureChartElementSize(opts: MeasureOptions): ElementSize {
  const {
    text,
    fontSize,
    fontFamily = 'sans-serif',
    fontWeight = 'normal',
    fontStyle = 'normal',
    paddingX = 16,
    paddingY = 8,
    lineHeight = 1.5,
    minWidth = 0,
    maxWidth = 400,
    skipTenCharMin = false,
  } = opts;

  const displayText = text || ' ';

  const lines = displayText.split('\n');
  const maxLineW = Math.max(...lines.map(l =>
    singleLineWidth(l || ' ', fontSize, fontWeight, fontStyle, fontFamily)
  ));

  const tenCharW = singleLineWidth('n'.repeat(10), fontSize, fontWeight, fontStyle, fontFamily);
  const computedMin = skipTenCharMin
    ? minWidth
    : Math.max(minWidth, Math.ceil(tenCharW) + paddingX);

  const naturalW = Math.ceil(maxLineW) + paddingX;
  const width = Math.round(Math.max(computedMin, Math.min(naturalW, maxWidth)));

  const height = measureWrappedHeight({
    text: displayText,
    fontSize,
    fontFamily,
    fontWeight,
    fontStyle,
    lineHeight,
    paddingX,
    paddingY,
    width,
  });

  return { width, height };
}

interface HeightOptions {
  text: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  lineHeight: number;
  paddingX: number;
  paddingY: number;
  width: number;
}

export function measureWrappedHeight(opts: HeightOptions): number {
  const { text, fontSize, fontFamily, fontWeight, fontStyle, lineHeight, paddingX, paddingY, width } = opts;
  const minH = Math.ceil(fontSize * lineHeight) + paddingY;

  try {
    const div = document.createElement('div');
    div.style.cssText = [
      'position:fixed',
      'visibility:hidden',
      'pointer-events:none',
      'top:-9999px',
      'left:-9999px',
      `width:${Math.max(1, width)}px`,
      `font-size:${fontSize}px`,
      `font-weight:${fontWeight}`,
      `font-style:${fontStyle}`,
      `font-family:${fontFamily}`,
      `line-height:${lineHeight}`,
      'white-space:pre-wrap',
      'overflow-wrap:break-word',
      'word-break:break-word',
      `padding:${paddingY / 2}px ${paddingX / 2}px`,
      'box-sizing:border-box',
    ].join(';');
    div.textContent = text || ' ';
    document.body.appendChild(div);
    const h = div.scrollHeight;
    document.body.removeChild(div);
    return Math.max(minH, h + 2);
  } catch {
    return minH;
  }
}

interface ElementConfig {
  fontSize: number;
  fontWeight: string;
  fontStyle: string;
  fontFamily: string;
  paddingX: number;
  paddingY: number;
  lineHeight: number;
  minWidth: number;
  tightMinWidth?: boolean;
}

const BASE_PADDING_X = 16;
const BASE_PADDING_Y = 4;

const ELEMENT_CONFIGS: Record<ChartElementType, ElementConfig> = {
  title:      { fontSize: 24, fontWeight: 'bold',   fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: BASE_PADDING_X, paddingY: BASE_PADDING_Y, lineHeight: 1.15, minWidth: 0 },
  tempo:      { fontSize: 14, fontWeight: 'normal', fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: 40,             paddingY: BASE_PADDING_Y, lineHeight: 1.3,  minWidth: 80 },
  key:        { fontSize: 14, fontWeight: 'normal', fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: 40,             paddingY: BASE_PADDING_Y, lineHeight: 1.3,  minWidth: 80 },
  section:    { fontSize: 14, fontWeight: 'bold',   fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: BASE_PADDING_X, paddingY: BASE_PADDING_Y, lineHeight: 1.2,  minWidth: 0 },
  chord:      { fontSize: 18, fontWeight: 'bold',   fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: BASE_PADDING_X, paddingY: BASE_PADDING_Y, lineHeight: 1.25, minWidth: 0 },
  lyrics:     { fontSize: 14, fontWeight: 'normal', fontStyle: 'italic', fontFamily: 'sans-serif',  paddingX: BASE_PADDING_X, paddingY: BASE_PADDING_Y, lineHeight: 1.3,  minWidth: 0 },
  notes:      { fontSize: 14, fontWeight: 'normal', fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: BASE_PADDING_X, paddingY: BASE_PADDING_Y, lineHeight: 1.3,  minWidth: 0 },
  freetext:   { fontSize: 14, fontWeight: 'normal', fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: BASE_PADDING_X, paddingY: BASE_PADDING_Y, lineHeight: 1.3,  minWidth: 0 },
  image:      { fontSize: 14, fontWeight: 'normal', fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: BASE_PADDING_X, paddingY: BASE_PADDING_Y, lineHeight: 1.3,  minWidth: 80 },
  divider:    { fontSize: 12, fontWeight: 'normal', fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: BASE_PADDING_X, paddingY: BASE_PADDING_Y, lineHeight: 1.5,  minWidth: 80 },
  repeat:     { fontSize: 18, fontWeight: 'bold',   fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: BASE_PADDING_X, paddingY: BASE_PADDING_Y, lineHeight: 1.25, minWidth: 0, tightMinWidth: true },
  link:       { fontSize: 14, fontWeight: 'normal', fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: 36,             paddingY: BASE_PADDING_Y, lineHeight: 1.2,  minWidth: 100 },
  songHeader: { fontSize: 20, fontWeight: 'bold',   fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: BASE_PADDING_X, paddingY: BASE_PADDING_Y, lineHeight: 1.2,  minWidth: 150 },
  songForm:   { fontSize: 14, fontWeight: 'normal', fontStyle: 'normal', fontFamily: 'sans-serif',  paddingX: BASE_PADDING_X, paddingY: BASE_PADDING_Y, lineHeight: 1.3,  minWidth: 0, tightMinWidth: true },
};

export function getElementDisplayText(type: ChartElementType, content: Record<string, any>): string {
  switch (type) {
    case 'title':   return content.text || 'Song Title';
    case 'tempo':   return `${content.bpm || 120} BPM`;
    case 'key':     return `Key: ${content.key || 'C'}`;
    case 'section': return (content.label || content.type || 'Verse').toUpperCase();
    case 'chord':
      if (content.mode === 'freeform') return content.text || content.chord || 'Am';
      return content.chord || 'Am';
    case 'lyrics':   return content.text || '';
    case 'notes':    return content.text || '';
    case 'freetext': return content.text || '';
    case 'divider':  return content.label || '';
    case 'repeat':   return `x${content.count || 2}${content.label ? ' ' + content.label : ''}`;
    case 'link':     return content.label || 'Reference Recording';
    case 'songHeader': return content.title || 'Song Title';
    case 'songForm': {
      const mode = (content.mode as string) || 'names';
      const activeEntries: string[] =
        mode === 'abc'
          ? (content.abcEntries ?? (mode === 'abc' ? content.entries : undefined) ?? ['A'])
          : (content.namesEntries ?? (mode === 'names' ? content.entries : undefined) ?? ['Intro']);
      const visible = activeEntries.filter((e: string) => e.trim());
      return visible.length > 0 ? visible.join(' → ') : (mode === 'abc' ? 'A' : 'Intro');
    }
    default:         return '';
  }
}

// Extra breathing room added to auto-sized widths so text never wraps prematurely.
// Applied after natural-width measurement and before maxWidth clamping so column
// constraints are always respected.
const AUTO_WIDTH_PADDING = 10;

// Element types that receive the extra width padding when auto-sizing.
const PADDED_AUTO_SIZE_TYPES = new Set<ChartElementType>([
  'title', 'section', 'chord', 'lyrics', 'freetext', 'notes', 'songHeader', 'songForm',
]);

export function autoSizeElement(
  type: ChartElementType,
  content: Record<string, any>,
  maxWidth: number,
): ElementSize {
  if (type === 'songHeader') {
    const title = content.title || 'Song Title';
    const subtitleParts: string[] = [];
    if (content.artist) subtitleParts.push(content.artist);
    if (content.key) subtitleParts.push(`Key: ${content.key}`);
    if (content.bpm) subtitleParts.push(`${content.bpm} BPM`);
    const subtitle = subtitleParts.join('  |  ');

    const titleMeasure = measureChartElementSize({
      text: title, fontSize: 24, fontWeight: 'bold', fontStyle: 'normal',
      fontFamily: 'sans-serif', paddingX: 16, paddingY: 4, lineHeight: 1.2,
      minWidth: 150, maxWidth,
    });
    const subtitleMeasure = subtitle
      ? measureChartElementSize({
          text: subtitle, fontSize: 16, fontWeight: 'normal', fontStyle: 'normal',
          fontFamily: 'sans-serif', paddingX: 16, paddingY: 4, lineHeight: 1.2,
          minWidth: 0, maxWidth,
        })
      : { width: 0, height: 0 };

    const rawW = Math.max(titleMeasure.width, subtitleMeasure.width, 150);
    return {
      width: Math.min(rawW + AUTO_WIDTH_PADDING, maxWidth),
      height: titleMeasure.height + subtitleMeasure.height,
    };
  }

  if (type === 'songForm') {
    const displayText = getElementDisplayText(type, content);
    const cfg = ELEMENT_CONFIGS[type];
    const { width, height } = measureChartElementSize({
      text: displayText,
      fontSize: cfg.fontSize, fontWeight: cfg.fontWeight, fontStyle: cfg.fontStyle,
      fontFamily: cfg.fontFamily,
      paddingX: cfg.paddingX, paddingY: cfg.paddingY,
      lineHeight: cfg.lineHeight, minWidth: 0, maxWidth,
      skipTenCharMin: cfg.tightMinWidth,
    });
    return { width: Math.min(width + AUTO_WIDTH_PADDING, maxWidth), height: height + 16 };
  }

  const cfg = ELEMENT_CONFIGS[type];
  const text = getElementDisplayText(type, content);
  const fontSize = (content.fontSize as number) || cfg.fontSize;
  const fontWeight = (content.fontWeight as string) || cfg.fontWeight;
  const fontStyle = (content.fontStyle as string) || cfg.fontStyle;
  const fontFamily = content.mode === 'freeform' ? 'monospace' : cfg.fontFamily;

  const result = measureChartElementSize({
    text,
    fontSize,
    fontWeight,
    fontStyle,
    fontFamily,
    paddingX: cfg.paddingX,
    paddingY: cfg.paddingY,
    lineHeight: cfg.lineHeight,
    minWidth: cfg.minWidth,
    maxWidth,
    skipTenCharMin: cfg.tightMinWidth,
  });

  if (PADDED_AUTO_SIZE_TYPES.has(type)) {
    return { width: Math.min(result.width + AUTO_WIDTH_PADDING, maxWidth), height: result.height };
  }
  return result;
}
